import React, { useMemo, useRef, useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import RobitoLogo from "../components/AppLogo";
import { apiPostPublic } from "../utils/api";

function TopBar({ onToggleMenu, menuOpen }) {
  return (
    <header className="header">
      <div className="left" />
      <div className="logo">
        <RobitoLogo />
      </div>
      <div className="right account-menu-anchor">
        <button
          className="icon-btn"
          onClick={onToggleMenu}
          title="Settings"
          aria-label="Settings"
          aria-expanded={menuOpen ? "true" : "false"}
          aria-haspopup="menu"
        >
          <i className="ti-settings" style={{ fontSize: 18, color: "white" }}></i>
        </button>
      </div>
    </header>
  );
}

function SiteList({ items = [], onSelect }) {
  return (
    <div className="list" role="list">
      {items.map((item) => (
        <article
          className="list-item"
          key={item.id}
          role="listitem"
          onClick={() => onSelect?.(item)}
          style={{ cursor: "pointer" }}
        >
          <div>
            <div className="item-title">{item.title}</div>
            <div className="item-sub">{item.description}</div>
            <div className="item-expiry">
              Expires: {new Date(item.expiry).toLocaleDateString()}
            </div>
          </div>
          <div className="chevron">›</div>
        </article>
      ))}
    </div>
  );
}

function EmptyState({ onAdd }) {
  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <button className="btn block add-site-btn" onClick={onAdd}>
          Buat Website
        </button>
      </div>

      <div className="empty-wrap">
        <div className="empty-card">
          <h3 style={{ margin: "4px 0" }}>Belum ada website</h3>
          <p className="small">
            Yuk buat website pertama kamu sekarang. Tinggal pilih paket, tentukan alamat website, dan isi informasi website kamu.
          </p>
        </div>
      </div>
    </div>
  );
}

/** Account dropdown */
function AccountMenu({ onClose, onLogout }) {
  const ref = useRef(null);

  // Close on outside click / Esc
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose?.();
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose?.();
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onClick);
    };
  }, [onClose]);

  return (
    <div ref={ref} className="account-menu" role="menu" aria-label="Account menu">
      <div className="section">
        <Link className="menu-item" to="/dashboard/account-settings" onClick={onClose}>
          <span className="ti-user" /> <span>Account Settings</span>
        </Link>
        <Link className="menu-item" to="/dashboard/billing-payment" onClick={onClose}>
          <span className="ti-credit-card" /> <span>Billing & Payments</span>
        </Link>
        <Link className="menu-item" to="/dashboard/referral" onClick={onClose}>
          <span className="ti-gift" /> <span>Referral</span>
        </Link>
        <a className="menu-item" href="/help-faq" target="_blank" rel="noopener noreferrer" onClick={onClose}>
          <span className="ti-help-alt" /> <span>Help & FAQ</span>
        </a>
        <a className="menu-item" href="/privacy-policy" target="_blank" rel="noopener noreferrer" onClick={onClose}>
          <span className="ti-shield" /> <span>Privacy Policy</span>
        </a>
        <a className="menu-item" href="/terms-of-service" target="_blank" rel="noopener noreferrer" onClick={onClose}>
          <span className="ti-agenda" /> <span>Terms of Service</span>
        </a>
      </div>

      <div className="divider" />

      {/* Logout pinned at bottom */}
      <button className="menu-item logout" onClick={onLogout}>
        <span className="ti-power-off" /> <span>Logout</span>
      </button>
    </div>
  );
}

function Dashboard() {
  const navigate = useNavigate();

  const [sites1] = useState([
    { id: "a1", title: "My First Website", description: "Personal blog", expiry: "2025-03-01" },
    { id: "b2", title: "Landing Page", description: "Product launch microsite", expiry: "2025-04-15" },
    { id: "c3", title: "Promo Page", description: "Seasonal event promo", expiry: "2025-05-30" },
  ]);

  const [sites] = useState([]);

  const [query, setQuery] = useState("");
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sites;
    return sites.filter(
      (s) =>
        s.title.toLowerCase().includes(q) ||
        s.description.toLowerCase().includes(q)
    );
  }, [sites, query]);

  // Dropdown
  const [menuOpen, setMenuOpen] = useState(false);
  const toggleMenu = () => setMenuOpen((v) => !v);
  const closeMenu = () => setMenuOpen(false);

  // Actions
  const handleAdd = () => navigate("/dashboard/sites/new");
  const handleSelect = (item) => navigate(`/dashboard/sites/${item.id}`);
  const handleAccount = () => toggleMenu();

  const handleLogout = async () => {
    try {
      const response = await apiPostPublic("logout.php");
      if (response.status === 'success') {
        navigate("/login");
      }
    } catch (error) {
      alert("Oops, something went wrong");
    }
  };

  return (
    <div className="app-shell">
      <TopBar onToggleMenu={handleAccount} menuOpen={menuOpen} />

      {/* Dropdown anchored to top-right; positioned via CSS */}
      {menuOpen && (
        <AccountMenu onClose={closeMenu} onLogout={handleLogout} />
      )}

      <main className="main">
        {filtered.length === 0 ? (
          <EmptyState onAdd={handleAdd} />
        ) : (
          <div>
            <div className="section-head">
              <button className="btn block add-site-btn" onClick={handleAdd}>
                Tambah Website
              </button>
            </div>

            <SiteList items={filtered} onSelect={handleSelect} />
          </div>
        )}
      </main>
    </div>
  );
}

export default Dashboard;
